//
//  useCsvDownloadList.ts
//
//  isana.net,inc. Proprietary and Confidential
//

import { useCallback, useEffect, useState } from "react";
import { getCsvDownloadList } from "../network/api";
import { CsvDownload, CsvDownloadListFetcher } from "../network/type";
import useProgress from "./useProgress";
import { CsvDownloadTableRow } from "../component/CsvDownloadTable";

const ROWS_PER_PAGE = 30;
const POLLING_INTERVAL_MS = 10000;

// fetchList差し替えでGHA CSV等の別エンドポイントにも対応する（省略時は従来どおり）
const useCsvDownloadList = (
  fetchList: CsvDownloadListFetcher = getCsvDownloadList
) => {
  const progressDialog = useProgress();
  const [fileList, setFileList] = useState<CsvDownload[]>([]);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);

  const fetchCsvDownloadList = useCallback(
    async (page: number): Promise<void> => {
      const response = await fetchList(page + 1);
      if (response.status === 200 && response.data) {
        setFileList(response.data.files);
        setTotal(response.data.total ?? 0);
      } else {
        setFileList([]);
        setTotal(0);
        console.error("Error fetching file list:", response);
      }
    },
    [fetchList]
  );

  useEffect(() => {
    progressDialog.show(async () => {
      await fetchCsvDownloadList(page);
    });
  }, [page, fetchCsvDownloadList]);

  const hasIncompleteFiles = fileList.some(
    (file) => (file.progress ?? 1.0) < 1.0
  );

  useEffect(() => {
    if (!hasIncompleteFiles) return;
    const intervalId = setInterval(() => {
      fetchCsvDownloadList(page);
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [page, fetchCsvDownloadList, hasIncompleteFiles]);

  const isValidFileName = (dateString: string): boolean => {
    // "{CSV種別}_YYYY-MM-DD_YYYY-MM-DD_YYYY-MM-DD-HHMMSS"の形式であるかをチェック
    const regex =
      /^.+_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}-\d{6}.*$/;
    return regex.test(dateString);
  };

  const formatFileNameShort = (fileName: string): string => {
    if (!isValidFileName(fileName)) return "";
    // ファイル名全体から拡張子のみ除去する
    const extIndex = fileName.lastIndexOf(".");
    if (extIndex === -1) {
      // 拡張子が存在しない場合、そのまま返す
      return fileName;
    }
    return fileName.slice(0, extIndex);
  };

  const formatCreatedTime = (fileName: string): string => {
    if (!isValidFileName(fileName)) return "";
    // ファイル名から日付部分を抽出してフォーマットする
    const lastUnderscore = fileName.lastIndexOf("_");
    const extIndex = fileName.lastIndexOf(".");
    const endIndex = extIndex === -1 ? fileName.length : extIndex; // 拡張子がない場合はファイル名の末尾まで
    const dateStr = fileName.slice(lastUnderscore + 1, endIndex);
    const match = dateStr.match(
      /^(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})(\d{2})$/
    );
    if (!match) return "";
    const [, year, month, day, hour, min, sec] = match;
    return `${year}/${month}/${day} ${hour}:${min}:${sec}`;
  };

  const displayedFiles: CsvDownloadTableRow[] = fileList.map((file) => ({
    fileName: file.fileName,
    fileNameShort: formatFileNameShort(file.fileName),
    createdTime: formatCreatedTime(file.fileName),
    progress: file.progress ?? 1.0,
  }));

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
  }, []);

  return {
    displayedFiles,
    count: total,
    showProgress: progressDialog.open,
    rowsPerPage: ROWS_PER_PAGE,
    page,
    handlePageChange,
  };
};

export default useCsvDownloadList;
